import type { FlagValues } from "@amtana/browser";
import type { Catalogs, Translate, Translation } from "@amtana/i18n/catalog";

// Every word on the Projects page, the top bar's switcher and the toasts of a
// project write, in English and Spanish. A screen reads them through
// `useProjectCopy`; a key with no Spanish entry falls back to English.

export type ProjectCopyKey = keyof typeof EN;
export type ProjectCatalog = typeof EN;

const EN = {
  screenTitle: "Projects · {product}",
  navProjects: "Projects",
  archived: "Archived",
  archive: "Archive",
  restore: "Restore",
  showArchived: "Show archived",
  archiveProjectTitle: "Archive {name}?",
  archiveProjectDescription:
    "Its analyses and other data stay reachable; it is just hidden from the list until restored.",
  archiveProjectFallbackTitle: "Archive this project?",
  descriptionAnalyses:
    "Keep each client or business apart: a project scopes its analyses. Prospects are shared.",
  descriptionAnalysesOutreach:
    "Keep each client or business apart: a project scopes its analyses, campaigns and email accounts. Prospects are shared.",
  descriptionAnalysesFilters:
    "Keep each client or business apart: a project scopes its analyses and filters. Prospects are shared.",
  descriptionAnalysesOutreachFilters:
    "Keep each client or business apart: a project scopes its analyses, campaigns, email accounts and filters. Prospects are shared.",
  breadcrumbSettings: "Settings",
  newProject: "New project",
  yourProjects: "Your projects",
  planUsage: "{used} of {max} on your plan",
  planUsageUnlimited: "{used} of unlimited on your plan",
  tableLabel: "Projects",
  columnName: "Name",
  columnCreated: "Created",
  columnActions: "Actions",
  pillCurrent: "Current",
  pillDefault: "Default",
  actionsFor: "Actions for {name}",
  switchToIt: "Switch to it",
  rename: "Rename",
  renameTitle: "Rename {name}",
  newProjectTitle: "New project",
  save: "Save",
  create: "Create",
  cancel: "Cancel",
  confirm: "Confirm",
  nameField: "Name",
  nameHint: "Up to {max} characters",
  namePlaceholder: "Client name",
  createDrawerTitle: "Create a new project",
  switchProject: "Switch project",
  defaultProjectName: "Default Project",
  switcherProjects: "Projects",
  createNewProject: "Create New Project",
  manageProjects: "Manage Projects",
  createdToast: "Project {name} created",
  couldNotCreate: "Could not create the project",
  renamedToast: "Project renamed",
  couldNotRename: "Could not rename the project",
  switchedToast: "Switched to {name}",
  couldNotSwitch: "Could not switch project",
  archivedToast: "Project archived",
  notArchivedToast: "Not archived",
  couldNotArchive: "Could not archive the project",
  restoredToast: "Project restored",
  couldNotRestore: "Could not restore the project",
} as const;

const ES: Translation<ProjectCatalog> = {
  screenTitle: "Proyectos · {product}",
  navProjects: "Proyectos",
  archived: "Archivado",
  archive: "Archivar",
  restore: "Restaurar",
  showArchived: "Mostrar archivados",
  archiveProjectTitle: "¿Archivar {name}?",
  archiveProjectDescription:
    "Sus análisis y demás datos siguen disponibles; solo se oculta de la lista hasta que se restaure.",
  archiveProjectFallbackTitle: "¿Archivar este proyecto?",
  descriptionAnalyses:
    "Mantén separado a cada cliente o negocio: un proyecto agrupa sus análisis. Los prospectos se comparten.",
  descriptionAnalysesOutreach:
    "Mantén separado a cada cliente o negocio: un proyecto agrupa sus análisis, campañas y cuentas de correo. Los prospectos se comparten.",
  descriptionAnalysesFilters:
    "Mantén separado a cada cliente o negocio: un proyecto agrupa sus análisis y filtros. Los prospectos se comparten.",
  descriptionAnalysesOutreachFilters:
    "Mantén separado a cada cliente o negocio: un proyecto agrupa sus análisis, campañas, cuentas de correo y filtros. Los prospectos se comparten.",
  breadcrumbSettings: "Configuración",
  newProject: "Nuevo proyecto",
  yourProjects: "Tus proyectos",
  planUsage: "{used} de {max} en tu plan",
  planUsageUnlimited: "{used}, sin límite en tu plan",
  tableLabel: "Proyectos",
  columnName: "Nombre",
  columnCreated: "Creado",
  columnActions: "Acciones",
  pillCurrent: "Actual",
  pillDefault: "Predeterminado",
  actionsFor: "Acciones para {name}",
  switchToIt: "Cambiar a este",
  rename: "Renombrar",
  renameTitle: "Renombrar {name}",
  newProjectTitle: "Nuevo proyecto",
  save: "Guardar",
  create: "Crear",
  cancel: "Cancelar",
  confirm: "Confirmar",
  nameField: "Nombre",
  nameHint: "Hasta {max} caracteres",
  namePlaceholder: "Nombre del cliente",
  createDrawerTitle: "Crear un proyecto nuevo",
  switchProject: "Cambiar de proyecto",
  defaultProjectName: "Proyecto predeterminado",
  switcherProjects: "Proyectos",
  createNewProject: "Crear proyecto nuevo",
  manageProjects: "Administrar proyectos",
  createdToast: "Proyecto {name} creado",
  couldNotCreate: "No se pudo crear el proyecto",
  renamedToast: "Proyecto renombrado",
  couldNotRename: "No se pudo renombrar el proyecto",
  switchedToast: "Cambiaste a {name}",
  couldNotSwitch: "No se pudo cambiar de proyecto",
  archivedToast: "Proyecto archivado",
  notArchivedToast: "No se archivó",
  couldNotArchive: "No se pudo archivar el proyecto",
  restoredToast: "Proyecto restaurado",
  couldNotRestore: "No se pudo restaurar el proyecto",
};

export const PROJECT_CATALOGS: Catalogs<ProjectCatalog> = { en: EN, es: ES };

/** The Projects page's description, naming only what a project scopes that the organization
 * can open (D206): campaigns and email accounts with "outreach", filters with "filters". */
export function projectsDescription(flags: FlagValues | undefined, t: Translate<ProjectCatalog>): string {
  const outreach = flags?.outreach === true;
  const filters = flags?.filters === true;
  if (outreach && filters) return t("descriptionAnalysesOutreachFilters");
  if (outreach) return t("descriptionAnalysesOutreach");
  return filters ? t("descriptionAnalysesFilters") : t("descriptionAnalyses");
}
